<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading…</div>
    <div v-if="loaded" class="root">

      <!-- Top ticker -->
      <div class="ticker">
        <div class="ticker-track">
          <span v-for="n in 8" :key="n">{{ heroPrimaryText }} &nbsp;◉&nbsp;   </span>
        </div>
      </div>

      <!-- Nav -->
      <nav class="nav">
        <img :src="lightLogoUrl" class="nav-logo" />
        <div class="nav-pill">
          <span class="nav-lo">{{ loName || "Loan Officer" }}</span>
          <span class="nav-plus">+</span>
          <span class="nav-re">{{ realtorName || "Realtor" }}</span>
        </div>
        <a class="nav-cta" :href="applyRightLink">{{ heroPrimaryText }} →</a>
      </nav>

      <!-- Hero block -->
      <section class="hero">
        <div class="hero-content">
  
          <h1 class="hero-h1" v-html="heroH1Html"></h1>
          <p class="hero-body">{{ heroBody }}</p>
          <div class="hero-btns">
            <a class="btn-pop" :href="applyRightLink">{{ heroPrimaryText }}</a>
            <a v-if="applyLeftLink" class="btn-soft" :href="applyLeftLink">{{ heroSecondaryText }}</a>
          </div>
        </div>
        <!-- Floating big number accent -->
        <div class="hero-bignum" aria-hidden="true">1<span class="bignum-dot">st</span></div>
      </section>

      <!-- Quick contact strip -->
      <div class="contact-strip">
        <a v-if="loinfo && loinfo.phone_number" class="cs-item" :href="'tel:' + loinfo.phone_number">
          <span class="cs-icon">☎</span> {{ loinfo.phone_number }}
        </a>
        <div class="cs-divider"></div>
        <a v-if="loinfo && loinfo.email" class="cs-item" :href="'mailto:' + loinfo.email">
          <span class="cs-icon">✉</span> {{ loinfo.email }}
        </a>
        <div class="cs-divider"></div>
        <a v-if="realtor && realtor.website_url" class="cs-item" :href="realtor.website_url" target="_blank" rel="noreferrer">
          <span class="cs-icon">↗</span> Partner site
        </a>
      </div>

      <!-- Profile cards -->
      <section class="cards">

        <div class="pcard lo-card">
          <div class="pcard-wash lo-wash"></div>
          <div class="pcard-body">
            <div class="pcard-img-ring lo-ring">
              <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="pcard-img" />
            </div>
            <div class="pcard-label">Loan Officer</div>
            <div class="pcard-name">{{ loName }}</div>
            <div class="pcard-role">{{ loTitle }}</div>
            <div v-if="loinfo && loinfo.nmls" class="pcard-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
            <div class="pcard-chips">
              <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="pchip">{{ loinfo.phone_number }}</a>
              <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="pchip">{{ loinfo.email }}</a>
            </div>
            <a class="pcard-cta lo-cta" :href="applyRightLink">{{ heroPrimaryText }}</a>
          </div>
        </div>

        <div class="pcard re-card">
          <div class="pcard-wash re-wash"></div>
          <div class="pcard-body">
            <div class="pcard-img-ring re-ring">
              <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="pcard-img" />
            </div>
            <div class="pcard-label re-lbl">Realtor Partner</div>
            <div class="pcard-name">{{ realtorName }}</div>
            <div class="pcard-role">{{ realtor && realtor.brokerage_name ? realtor.brokerage_name : "" }}</div>
            <div class="pcard-chips">
              <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="pchip re-pchip">{{ realtor.phone_number }}</a>
              <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="pchip re-pchip">{{ realtor.email }}</a>
              <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="pchip re-pchip" target="_blank" rel="noreferrer">Website →</a>
            </div>
          </div>
        </div>

      </section>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending:        { primary: "#eb610a", accent: "#646363" },
  radiantmortgage:    { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage:     { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam:         { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage:         { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage:     { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial:  { primary: "#0b1726", accent: "#c69c39" },
};
const DBA_BASE = "https://api.goldstarfinancial.com/DBA";
function dbaLogo(brandKey, variant = "primary") { return `${DBA_BASE}/${brandKey}/${variant}.png`; }
function getBrandKey(email) {
  if (!email) return null;
  const d = email.slice(email.indexOf("@") + 1).toLowerCase();
  return d.split(".")[0] || null;
}
export default {
  name: "CoBrandPulse",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    lightLogoUrl() { return dbaLogo(this.brandKey, "primary"); },
    
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Ready to get<br/><em>started?</em>";
      const parts = h.split(",");
      if (parts.length > 1) {
        const a = parts[0].trim();
        const b = parts.slice(1).join(",").trim();
        return `${a}<br/><em>${b}</em>`;
      }
      return h;
    },
    
    heroHeading() { return this.loinfo?.text_section_heading || "About"; },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      const p = [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim();
      return p || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Unbounded:wght@400;700;900&family=DM+Sans:wght@300;400;500&display=swap');

.page {
  min-height: 100vh;
  background: #f5f3ff;
  color: #0f0f0f;
  font-family: 'DM Sans', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --bg: #f5f3ff;
  --hairline: rgba(15,15,15,0.10);
  --muted: #6b7280;
  --a-tint: color-mix(in srgb, var(--a) 14%, #f5f3ff);
  --p-tint: color-mix(in srgb, var(--p) 6%, #f5f3ff);
  overflow-x: hidden;
}
.root { display: flex; flex-direction: column; min-height: 100vh; }

/* TICKER */
.ticker {
  background: var(--p); color: var(--a);
  font-family: 'Unbounded', sans-serif;
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  overflow: hidden; white-space: nowrap;
  padding: 8px 0;
}
.ticker-track {
  display: inline-block;
  animation: ticker 24s linear infinite;
}
@keyframes ticker {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}

/* NAV */
.nav {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 40px;
  background: #fff;
  border-bottom: 2px solid var(--p);
}
.nav-logo { height: 34px; object-fit: contain; }
.nav-pill {
  display: flex; align-items: center; gap: 8px;
  background: var(--p-tint); border: 1.5px solid var(--hairline);
  padding: 6px 16px; border-radius: 50px; font-size: 13px;
}
.nav-lo { font-weight: 600; color: var(--p); }
.nav-plus { color: var(--a); font-size: 18px; line-height: 1; font-weight: 700; }
.nav-re { color: var(--muted); }
.nav-cta {
  font-family: 'Unbounded', sans-serif;
  font-size: 11px; font-weight: 700; text-decoration: none;
  background: var(--a); color: var(--p);
  padding: 10px 20px; border-radius: 50px;
  transition: transform 0.18s, box-shadow 0.18s;
}
.nav-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 20px color-mix(in srgb, var(--a) 40%, transparent); }

/* HERO */
.hero {
  background: #fff; border-bottom: 2px solid var(--p);
  padding: 64px 40px 64px;
  display: grid; grid-template-columns: 1fr auto;
  align-items: center; gap: 40px;
  position: relative; overflow: hidden;
}
.hero-tag {
  display: inline-block; font-family: 'Unbounded', sans-serif;
  font-size: 9px; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase;
  color: #fff; background: var(--p); padding: 5px 14px; border-radius: 50px;
  margin-bottom: 18px;
}
.hero-h1 {
  font-family: 'Unbounded', sans-serif;
  font-size: clamp(32px, 5vw, 64px);
  font-weight: 900; line-height: 1.0;
  color: var(--p); margin: 0 0 20px;
  letter-spacing: -0.02em;
}
.h1-pop { color: var(--a); }
.hero-body { font-size: 15px; line-height: 1.72; color: var(--muted);  margin-bottom: 28px; }
.hero-btns { display: flex; gap: 10px; flex-wrap: wrap; }
.btn-pop {
  font-family: 'Unbounded', sans-serif;
  font-size: 11px; font-weight: 700; text-decoration: none;
  background: var(--p); color: #fff;
  padding: 14px 28px; border-radius: 14px;
  transition: transform 0.18s, box-shadow 0.18s;
}
.btn-pop:hover { transform: translateY(-2px); box-shadow: 0 10px 26px color-mix(in srgb, var(--p) 28%, transparent); }
.btn-soft {
  font-size: 14px; font-weight: 500; text-decoration: none;
  color: var(--p); background: var(--p-tint);
  border: 1.5px solid var(--hairline);
  padding: 14px 28px; border-radius: 14px; transition: background 0.18s;
}
.btn-soft:hover { background: color-mix(in srgb, var(--p) 10%, var(--bg)); }

/* Big decorative number */
.hero-bignum {
  font-family: 'Unbounded', sans-serif;
  font-size: clamp(100px, 16vw, 200px);
  font-weight: 900; line-height: 1;
  color: var(--p); opacity: 0.04;
  user-select: none; pointer-events: none;
  position: absolute; right: -10px; top: 50%; transform: translateY(-50%);
  letter-spacing: -0.05em;
}
.bignum-dot { font-size: 0.35em; vertical-align: super; opacity: 1; }

/* CONTACT STRIP */
.contact-strip {
  display: flex; align-items: center; gap: 0;
  background: var(--a); padding: 14px 40px;
  flex-wrap: wrap;
}
.cs-item {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; font-weight: 500; text-decoration: none;
  color: var(--p); padding: 0 20px 0 0;
}
.cs-item:hover { text-decoration: underline; }
.cs-icon { font-size: 14px; }
.cs-divider { width: 1px; height: 18px; background: color-mix(in srgb, var(--p) 25%, transparent); margin-right: 20px; }

/* CARDS */
.cards {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 20px; padding: 36px 40px 48px;
  background: var(--bg);
}
.pcard {
  background: #fff; border-radius: 24px;
  border: 2px solid var(--p);
  overflow: hidden;
  box-shadow: 6px 6px 0 var(--p);
  transition: transform 0.18s, box-shadow 0.18s;
}
.pcard:hover { transform: translate(-3px, -3px); box-shadow: 9px 9px 0 var(--p); }
.re-card { box-shadow: 6px 6px 0 var(--a); border-color: var(--a); }
.re-card:hover { box-shadow: 9px 9px 0 var(--a); }

.pcard-wash { height: 80px; }
.lo-wash { background: color-mix(in srgb, var(--p) 8%, #fff); }
.re-wash { background: color-mix(in srgb, var(--a) 12%, #fff); }

.pcard-body { padding: 0 22px 22px; }
.pcard-img-ring {
  width: 72px; height: 72px; border-radius: 50%;
  border: 3px solid var(--p);
  margin-top: -36px; margin-bottom: 12px;
  background: var(--p-tint); overflow: hidden;
}
.re-ring { border-color: var(--a); }
.pcard-img { width: 100%; height: 100%; object-fit: cover; }
.pcard-label {
  font-family: 'Unbounded', sans-serif;
  font-size: 8px; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase;
  color: var(--p); margin-bottom: 6px;
}
.re-lbl { color: color-mix(in srgb, var(--a) 85%, black); }
.pcard-name { font-family: 'Unbounded', sans-serif; font-size: 16px; font-weight: 700; color: var(--p); line-height: 1.2; margin-bottom: 4px; }
.pcard-role { font-size: 12px; color: var(--muted); margin-bottom: 2px; }
.pcard-nmls { font-size: 11px; color: color-mix(in srgb, var(--muted) 65%, transparent); margin-bottom: 14px; }
.pcard-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 16px; }
.pchip {
  font-size: 11px; font-weight: 500; text-decoration: none;
  color: var(--p); background: var(--p-tint);
  padding: 5px 12px; border-radius: 50px;
  border: 1px solid var(--hairline); transition: background 0.18s;
}
.pchip:hover { background: color-mix(in srgb, var(--p) 10%, var(--bg)); }
.re-pchip { color: color-mix(in srgb, var(--a) 85%, black); background: var(--a-tint); border-color: color-mix(in srgb, var(--a) 20%, transparent); }
.re-pchip:hover { background: color-mix(in srgb, var(--a) 20%, var(--bg)); }
.pcard-cta {
  display: block; text-align: center; text-decoration: none;
  font-family: 'Unbounded', sans-serif;
  font-size: 10px; font-weight: 700; padding: 12px;
  border-radius: 12px; transition: opacity 0.18s;
}
.lo-cta { background: var(--p); color: #fff; }
.lo-cta:hover { opacity: 0.85; }

@media (max-width: 700px) {
  .cards { grid-template-columns: 1fr; padding: 24px 20px; }
  .hero { grid-template-columns: 1fr; padding: 44px 24px; }
  .hero-bignum { display: none; }
  .nav { padding: 14px 20px; }
  .nav-pill { display: none; }
  .contact-strip { padding: 12px 20px; gap: 8px; }
}
</style>
